import { apiClient, type Paginated } from '@/core/api';
import type {
  ClassListParams,
  CreateClassInput,
  JoinClassResponse,
  JoinCodeAction,
  JoinCodeDto,
  PromoteClassInput,
  SchoolClassDetailDto,
  SchoolClassDto,
  SchoolClassWriteDto,
  UpdateClassInput,
} from './types';

/** `GET /api/classes/` — all classes for admin/teacher; enrolled ones (reduced
 *  payload) for pupils. */
export async function getClasses(params: ClassListParams = {}): Promise<Paginated<SchoolClassDto>> {
  const { data } = await apiClient.get<Paginated<SchoolClassDto>>('/classes/', { params });
  return data;
}

/** `GET /api/classes/{public_id}/` — detail (roster included for admin/teacher). */
export async function getClass(publicId: string): Promise<SchoolClassDetailDto> {
  const { data } = await apiClient.get<SchoolClassDetailDto>(`/classes/${publicId}/`);
  return data;
}

/** `POST /api/classes/` — admin or teacher (a creating teacher is auto-assigned). */
export async function createClass(input: CreateClassInput): Promise<SchoolClassWriteDto> {
  const { data } = await apiClient.post<SchoolClassWriteDto>('/classes/', input);
  return data;
}

/** `PATCH /api/classes/{public_id}/` — admin or a teacher of the class.
 *  Response is the write serializer read-back (`teachers` as public_ids). */
export async function updateClass(
  publicId: string,
  input: UpdateClassInput
): Promise<SchoolClassWriteDto> {
  const { data } = await apiClient.patch<SchoolClassWriteDto>(`/classes/${publicId}/`, input);
  return data;
}

/** `DELETE /api/classes/{public_id}/` — archives (never hard-deletes, C4). */
export async function archiveClass(publicId: string): Promise<void> {
  await apiClient.delete(`/classes/${publicId}/`);
}

/** `POST /api/classes/{public_id}/unarchive/` — the inverse of DELETE; admin
 *  or a teacher of the class (server-enforced). Returns the refreshed detail. */
export async function promoteClass(
  publicId: string,
  input: PromoteClassInput
): Promise<SchoolClassDetailDto> {
  const { data } = await apiClient.post<SchoolClassDetailDto>(
    `/classes/${publicId}/promote/`,
    input
  );
  return data;
}

export async function unarchiveClass(publicId: string): Promise<SchoolClassDetailDto> {
  const { data } = await apiClient.post<SchoolClassDetailDto>(`/classes/${publicId}/unarchive/`);
  return data;
}

/** `POST /api/classes/{public_id}/enroll/` — returns the refreshed detail. */
export async function enrollPupil(
  classPublicId: string,
  memberPublicId: string
): Promise<SchoolClassDetailDto> {
  const { data } = await apiClient.post<SchoolClassDetailDto>(`/classes/${classPublicId}/enroll/`, {
    member_public_id: memberPublicId,
  });
  return data;
}

/** `POST /api/classes/{public_id}/unenroll/` — returns the refreshed detail. */
export async function unenrollPupil(
  classPublicId: string,
  memberPublicId: string
): Promise<SchoolClassDetailDto> {
  const { data } = await apiClient.post<SchoolClassDetailDto>(
    `/classes/${classPublicId}/unenroll/`,
    { member_public_id: memberPublicId }
  );
  return data;
}

/**
 * `POST /api/classes/{public_id}/join-code/` — create/rotate/enable/disable.
 * All actions are idempotent on the backend (ADR-008); `create` doubles as
 * "get or create" since the API exposes no separate read endpoint.
 */
export async function joinCodeAction(
  classPublicId: string,
  action: JoinCodeAction
): Promise<JoinCodeDto> {
  const { data } = await apiClient.post<JoinCodeDto>(`/classes/${classPublicId}/join-code/`, {
    action,
  });
  return data;
}

/** `POST /api/classes/join/` — join by code. The code resolves the school, so
 *  no X-School header is required (a fresh user becomes a pupil Member). */
export async function joinClassByCode(code: string): Promise<JoinClassResponse> {
  const { data } = await apiClient.post<JoinClassResponse>('/classes/join/', { code });
  return data;
}
